import { useRef, useState } from "react";
import { Alert, Button, DetailList, Dialog, Field } from "../../../ui";
import type { FieldElement } from "../../../ui";
import { failure } from "../../workspace/behavior";
import type { Schemas } from "../../workspace/api.generated";
import { businessCodeLabel, money } from "../../workspace/model";
import { PageHeader, Panel, StateNotice, handleAdminLinkNavigation, statusBadge } from "../../workspace/shared";
import { GuidedActionButton } from "../../workspace/GuidedActionButton";
import { reconciliationResolutionSchema } from "../schema";

type QueryState = "ready" | "loading" | "error" | "denied" | "not-found";

export interface ReconciliationIssueDetailViewProps {
  readonly urlRunId: number;
  readonly issueId: number;
  readonly issue?: Schemas["ReconciliationIssueView"];
  readonly state: QueryState;
  readonly canManage: boolean;
  readonly backHref: string;
  readonly backLabel: string;
  readonly onBack: () => void;
  readonly actualRunHref?: string;
  readonly relatedHref?: string;
  readonly relatedLabel?: string;
  readonly relatedDenied?: boolean;
  readonly notice?: string;
  readonly onOpenActualRun?: () => void;
  readonly onOpenRelated?: () => void;
  readonly onRetry: () => void;
  readonly onResolve: (memo: string) => Promise<Schemas["ReconciliationIssueView"]>;
}

export function ReconciliationIssueDetailView(props: ReconciliationIssueDetailViewProps) {
  const [resolutionOpen, setResolutionOpen] = useState(false);
  const [memo, setMemo] = useState("");
  const [memoError, setMemoError] = useState("");
  const [requestError, setRequestError] = useState("");
  const [pending, setPending] = useState(false);
  const memoRef = useRef<FieldElement | null>(null);
  const mismatch = Boolean(props.issue && props.issue.reconciliationRunId !== props.urlRunId);

  const submit = async () => {
    if (pending) return;
    const parsed = reconciliationResolutionSchema.safeParse({ memo });
    if (!parsed.success) {
      setMemoError(parsed.error.issues[0]?.message ?? "해결 메모를 확인해 주세요.");
      memoRef.current?.focus();
      return;
    }
    setPending(true);
    setRequestError("");
    try {
      await props.onResolve(parsed.data.memo.trim());
      setResolutionOpen(false);
      setMemo("");
    } catch (cause) {
      setRequestError(failure(cause).message);
    } finally {
      setPending(false);
    }
  };

  return <>
    <PageHeader
      id="ADM-S31"
      title={`대사 이슈 ${props.issueId}`}
      description="소속 실행을 확인한 뒤 내부 기록과 Provider 결과, 해결 근거를 확인합니다."
      breadcrumbs={[
        { label: "대사 관리", href: "/operations/reconciliations?tab=issues" },
        { label: `실행 ${props.urlRunId}`, href: `/operations/reconciliations/${props.urlRunId}` },
        { label: `이슈 ${props.issueId}`, href: `/operations/reconciliations/${props.urlRunId}/issues/${props.issueId}` },
      ]}
      backAction={{ label: props.backLabel, href: props.backHref, onClick: props.onBack }}
    />
    {props.notice ? <Alert persistent variant="information" title="이슈 처리 결과">{props.notice}</Alert> : null}
    {props.state === "loading" ? <p role="status">대사 이슈를 불러오는 중입니다.</p>
      : props.state === "denied" ? <StateNotice state="denied" />
        : props.state === "not-found" ? <StateNotice state="not-found" />
        : props.state === "error" ? <Alert persistent variant="danger" title="대사 이슈를 불러오지 못했습니다" action={<Button size="small" variant="quiet" onClick={props.onRetry}>다시 조회</Button>}>소속 실행과 최신 상태를 다시 확인해 주세요.</Alert>
          : mismatch && props.issue ? <Alert
            persistent
            variant="warning"
            title="이 이슈는 다른 대사 실행에 속합니다"
            action={props.actualRunHref ? <a className="admin-row-link" href={props.actualRunHref} onClick={props.onOpenActualRun ? (event) => handleAdminLinkNavigation(event, props.onOpenActualRun!) : undefined}>실제 소속 실행에서 열기</a> : undefined}
          >
            요청한 실행 ID는 {props.urlRunId}, 서버가 확인한 실제 소속 실행 ID는 {props.issue.reconciliationRunId}입니다. 실제 소속을 열기 전에는 상세 값과 해결 작업을 표시하지 않습니다.
          </Alert>
            : props.issue ? <>
              <Panel title={`불일치 이슈 ${props.issue.id}`} description={`대사 실행 ID ${props.issue.reconciliationRunId}`}>
                <DetailList items={[
                  { label: "불일치 유형", value: businessCodeLabel(props.issue.issueType) },
                  { label: "업무 대상", value: props.issue.businessRefType ? `${businessCodeLabel(props.issue.businessRefType)} ${props.issue.businessRefId ?? "—"}` : "—" },
                  { label: "내부 금액", value: money(props.issue.internalAmount) },
                  { label: "Provider 금액", value: money(props.issue.providerAmount) },
                  { label: "내부 상태", value: props.issue.internalStatus ? businessCodeLabel(props.issue.internalStatus) : "—" },
                  { label: "Provider 상태", value: props.issue.providerStatus ? businessCodeLabel(props.issue.providerStatus) : "—" },
                  { label: "이슈 상태", value: statusBadge(props.issue.status) },
                  { label: "해결 메모", value: props.issue.resolutionMemo || "기록 없음" },
                ]} />
                <div className="admin-detail-actions">
                  {props.relatedHref && props.relatedLabel ? <a className="admin-row-link" href={props.relatedHref} onClick={props.onOpenRelated ? (event) => handleAdminLinkNavigation(event, props.onOpenRelated!) : undefined}>{props.relatedLabel}</a> : null}
                  {props.relatedDenied ? <span>관련 업무 조회 권한 없음</span> : null}
                  <GuidedActionButton size="small" blockedReason={!props.canManage ? "이슈 해결 권한이 없습니다. 권한 관리자에게 요청해 주세요." : props.issue.status !== "OPEN" ? "이미 해결된 이슈입니다. 해결 메모와 상태 이력을 확인해 주세요." : null} onClick={() => { setResolutionOpen(true); setRequestError(""); setMemoError(""); }}>해결 기록</GuidedActionButton>
                </div>
                {props.issue.status !== "OPEN" ? <p>이미 해결된 이슈입니다. 서버에 기록된 메모를 확인해 주세요.</p> : null}
              </Panel>
            </> : null}

    <Dialog
      open={resolutionOpen}
      size="medium"
      title="이슈 해결을 기록할까요?"
      description={`이슈 ID ${props.issueId} · 대사 실행 ID ${props.urlRunId}`}
      closeDisabled={pending}
      onClose={() => { if (!pending) setResolutionOpen(false); }}
      secondaryAction={<Button variant="quiet" disabled={pending} onClick={() => setResolutionOpen(false)}>취소</Button>}
      primaryAction={<GuidedActionButton isLoading={pending} blockedReason={!props.canManage ? "이슈 해결 권한이 없습니다. 권한 관리자에게 요청해 주세요." : props.issue?.status !== "OPEN" ? "열린 이슈만 해결할 수 있습니다. 최신 상태를 조회해 주세요." : null} onClick={() => void submit()}>해결 기록 요청</GuidedActionButton>}
    >
      {requestError ? <Alert persistent variant="danger" title="이슈를 해결하지 못했습니다">{requestError} 해결 메모를 유지했습니다.</Alert> : null}
      <Field ref={memoRef} compact control="textarea" label="해결 메모" required maxLength={2000} value={memo} error={memoError} onChange={(event) => { setMemo(event.currentTarget.value); setMemoError(""); }} />
    </Dialog>
  </>;
}
